import { TypeIcon } from '../sidebar/TypeIcon'

/**
 * ChipLabel — the shared chip-shape renderer for the one-chip-seam
 * (design.md CEO round 1, Rule 3): icon + colour + kenning.
 *
 * Consumed by:
 *  - Picker dropdown rows (EntityReferenceEditor)
 *  - Selected reference value trigger (EntityReferenceEditor)
 *  - Kanban column headers (future — when icon/color kinds land)
 *  - Every entity-reference cell value (reference.tsx ReferenceChips)
 *
 * Falls back gracefully when data is absent: icon=null → no icon element,
 * empty kenning → "(unnamed)". Never shows a UUID.
 */
export function ChipLabel({
  kenning,
  icon,
  color,
  className = '',
  placeholder = false,
  emptyLabel = '(unnamed)',
  iconOnly = false,
}: {
  kenning: string
  icon: string | null | undefined
  color: string | null | undefined
  className?: string
  /** Draw a neutral glyph where the target type declares no icon, so every
   *  chip in a list carries a mark (a Tariff chip looks like a Meter chip). */
  placeholder?: boolean
  /** Shown when the kenning is empty (reference cells say "unresolved"). */
  emptyLabel?: string
  /** Draw the mark only — a compacted chip (SEN-205) whose label lives in
   *  screen-reader text and the hover title instead. */
  iconOnly?: boolean
}) {
  const displayText = kenning || emptyLabel
  return (
    <span title={displayText} className={`inline-flex max-w-full items-center gap-1.5 truncate ${className}`}>
      {!icon && placeholder && (
        <span data-testid="chip-icon-placeholder" className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
          <span className="h-3 w-3 rounded-sm border border-muted-foreground/60" />
        </span>
      )}
      {icon && (
        <span
          data-testid="chip-icon"
          style={color ? { color } : undefined}
          className={`inline-flex h-4 w-4 shrink-0 items-center justify-center ${color ? '' : 'text-muted-foreground'}`}
        >
          <TypeIcon icon={icon} name={kenning || '?'} color={color} />
        </span>
      )}
      {!iconOnly && <span className="truncate">{displayText}</span>}
    </span>
  )
}
